'use client';

import { useState, useContext, type FC } from 'react';
import { Alert, Button, ButtonGroup, Flex, Dialog } from '@/_components';
import { deleteQuestionnaire } from '@/_api/admin-questionnaires';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import type { DeleteQuestionnairesDialogProps } from './DeleteQuestionnairesDialog.types';

export const DeleteQuestionnairesDialog: FC<
  DeleteQuestionnairesDialogProps
> = ({
  isOpen,
  loadQuestionnaires,
  onClose,
  selectedQuestionnaires,
  setSelectedQuestionnaires,
}) => {
  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);

  const [isLoading, setIsLoading] = useState(false);

  const onYes = async () => {
    try {
      setIsLoading(true);
      const promises: Array<any> = [];
      const deletedIds = new Set(selectedQuestionnaires.map((q) => q.id));

      selectedQuestionnaires.forEach((questionnaire) => {
        promises.push(
          async () =>
            await deleteQuestionnaire({
              companyId: questionnaire.company_id,
              id: questionnaire.id,
              token,
            }),
        );
      });

      await Promise.all(promises.map((p) => p()));
      await loadQuestionnaires();

      // Remove only the deleted questionnaires from the selection state
      setSelectedQuestionnaires((prev) =>
        prev.filter((q) => !deletedIds.has(q.id)),
      );

      addToast('success', 'Questionnaire(s) successfully deleted');
    } catch (error) {
      addToast(
        'error',
        'Something may have gone wrong. Please refresh the page to see if the questionnaire(s) were successfully deleted.',
        { error },
      );
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  return (
    <Dialog
      isNarrow={true}
      isOpen={isOpen}
      onClose={onClose}
      title="Confirmation"
    >
      <Dialog.Content>
        <Flex isColumn={true} gap={8}>
          <Alert type="danger" icon="warning">
            Are you sure you want to delete {selectedQuestionnaires.length}{' '}
            questionnaire(s)?
          </Alert>

          <ButtonGroup align="right" isSameLine>
            <Button
              intent="primary"
              isDisabled={isLoading}
              onClick={onYes}
              value="Yes"
            />

            <Button
              intent="danger-secondary"
              isDisabled={isLoading}
              onClick={onClose}
              value="No"
            />
          </ButtonGroup>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
};
